// src/pages/AdminApprovalsPage.js
import React, { useEffect, useState } from 'react'
import SidebarLayout from '../components/SidebarLayout'
import { supabase } from '../supabaseClient'

function AdminApprovalsPage() {
  const [betaRequests, setBetaRequests] = useState([])
  const [pendingUsers, setPendingUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [savingIds, setSavingIds] = useState([])
  const [error, setError] = useState(null)
  const [roleSelections, setRoleSelections] = useState({})

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [betaResult, usersResult] = await Promise.all([
        supabase
          .from('beta_invites')
          .select(`
            id,
            name,
            email,
            practice,
            role,
            software,
            created_at,
            status
          `)
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),

        supabase
          .from('users')
          .select('id, full_name, role, created_at')
          .eq('role', 'pending')
          .order('created_at', { ascending: true }),
      ])

      if (betaResult.error) {
        console.error('Error loading beta requests:', betaResult.error)
        throw new Error('Unable to load beta requests.')
      }

      if (usersResult.error) {
        console.error('Error loading pending users:', usersResult.error)
        throw new Error('Unable to load pending users.')
      }

      setBetaRequests(betaResult.data || [])
      setPendingUsers(usersResult.data || [])
    } catch (err) {
      console.error(err)
      setError(err.message || 'Unable to load approvals data.')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = (entityId, newRole) => {
    setRoleSelections((prev) => ({
      ...prev,
      [entityId]: newRole,
    }))
  }

  const handleApprovePendingUser = async (userId) => {
    const selectedRole = roleSelections[`user-${userId}`] || 'standard'

    setSavingIds((prev) => [...prev, `user-${userId}`])
    setError(null)

    const { error } = await supabase
      .from('users')
      .update({ role: selectedRole })
      .eq('id', userId)

    if (error) {
      console.error('Error updating user role:', error)
      setError('There was an error approving this user. Please try again.')
      setSavingIds((prev) => prev.filter((id) => id !== `user-${userId}`))
      return
    }

    setPendingUsers((prev) => prev.filter((u) => u.id !== userId))
    setSavingIds((prev) => prev.filter((id) => id !== `user-${userId}`))
  }

  const handleApproveBetaRequest = async (inviteId) => {
    const selectedRole = roleSelections[`invite-${inviteId}`] || 'standard'

    setSavingIds((prev) => [...prev, `invite-${inviteId}`])
    setError(null)

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      const response = await fetch('/api/admin/approve-beta-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          inviteId,
          role: selectedRole,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to approve beta request.')
      }

      setBetaRequests((prev) => prev.filter((invite) => invite.id !== inviteId))
    } catch (err) {
      console.error('Error approving beta request:', err)
      setError(err.message || 'There was an error approving this beta request.')
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== `invite-${inviteId}`))
    }
  }

  const formatDate = (value) => {
    if (!value) return '-'
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const sectionCardStyle = {
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    marginBottom: '2rem',
  }

  const thStyle = {
    textAlign: 'left',
    padding: '0.75rem 1rem',
  }

  const tdStyle = {
    padding: '0.75rem 1rem',
    borderTop: '1px solid #e5e7eb',
  }

  const selectStyle = {
    padding: '0.35rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '0.85rem',
  }

  const buttonStyle = (isSaving) => ({
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    border: 'none',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: isSaving ? 'default' : 'pointer',
    backgroundColor: isSaving ? '#9ca3af' : '#22c55e',
    color: '#ffffff',
  })

  return (
    <SidebarLayout>
      <div style={{ padding: '1.5rem 2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
          User Approvals
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
          Review beta access requests and approve pending users by assigning them a role.
        </p>

        {error && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div>Loading approvals…</div>
        ) : (
          <>
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Pending Beta Requests
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                Approving a beta request should create the user in Supabase Auth and in your
                public users table.
              </p>

              {betaRequests.length === 0 ? (
                <div style={{ color: '#6b7280' }}>No pending beta requests at this time.</div>
              ) : (
                <div style={sectionCardStyle}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.9rem',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Practice</th>
                        <th style={thStyle}>Software</th>
                        <th style={thStyle}>Requested At</th>
                        <th style={thStyle}>Assign Role</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {betaRequests.map((invite) => {
                        const saveKey = `invite-${invite.id}`
                        const isSaving = savingIds.includes(saveKey)
                        const selection = roleSelections[saveKey] || 'standard'

                        return (
                          <tr key={invite.id}>
                            <td style={tdStyle}>{invite.name || '-'}</td>
                            <td style={tdStyle}>{invite.email || '-'}</td>
                            <td style={tdStyle}>{invite.practice || '-'}</td>
                            <td style={tdStyle}>{invite.software || '-'}</td>
                            <td style={{ ...tdStyle, color: '#6b7280' }}>
                              {formatDate(invite.created_at)}
                            </td>
                            <td style={tdStyle}>
                              <select
                                value={selection}
                                onChange={(e) => handleRoleChange(saveKey, e.target.value)}
                                style={selectStyle}
                                disabled={isSaving}
                              >
                                <option value="standard">Standard</option>
                                <option value="admin">Admin</option>
                                <option value="demo">Demo</option>
                              </select>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <button
                                onClick={() => handleApproveBetaRequest(invite.id)}
                                disabled={isSaving}
                                style={buttonStyle(isSaving)}
                              >
                                {isSaving ? 'Saving…' : 'Approve'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Pending Existing Users
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                These are users already in your users table with a pending role.
              </p>

              {pendingUsers.length === 0 ? (
                <div style={{ color: '#6b7280' }}>No pending users at this time.</div>
              ) : (
                <div style={sectionCardStyle}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.9rem',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>User ID</th>
                        <th style={thStyle}>Requested At</th>
                        <th style={thStyle}>Assign Role</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map((user) => {
                        const saveKey = `user-${user.id}`
                        const isSaving = savingIds.includes(saveKey)
                        const selection = roleSelections[saveKey] || 'standard'

                        return (
                          <tr key={user.id}>
                            <td style={tdStyle}>
                              {user.full_name || <span style={{ color: '#9ca3af' }}>No name</span>}
                            </td>
                            <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{user.id}</td>
                            <td style={{ ...tdStyle, color: '#6b7280' }}>
                              {formatDate(user.created_at)}
                            </td>
                            <td style={tdStyle}>
                              <select
                                value={selection}
                                onChange={(e) => handleRoleChange(saveKey, e.target.value)}
                                style={selectStyle}
                                disabled={isSaving}
                              >
                                <option value="standard">Standard</option>
                                <option value="admin">Admin</option>
                                <option value="demo">Demo</option>
                              </select>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <button
                                onClick={() => handleApprovePendingUser(user.id)}
                                disabled={isSaving}
                                style={buttonStyle(isSaving)}
                              >
                                {isSaving ? 'Saving…' : 'Approve'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SidebarLayout>
  )
}

export default AdminApprovalsPage
